/*********************************************************************/
/*  src/App.jsx  — optimized shared components for member/company_admin */
/*********************************************************************/
import React, { Suspense, lazy, useMemo } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { NotificationProvider } from "./components/NotificationModule";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import ProtectedRoute from "./components/ProtectedRoute";
import SetPassword from "./pages/SetPassword";
import ManageCompanyPage from "./pages/ManageCompanyPage";
import AdminCompaniesPage from "./pages/AdminCompaniesPage";
import CmsEditor from "./pages/admin/CmsEditor";
import QuestionBank from "./pages/admin/QuestionBank";

/* CMS-backed simple pages (specialised wrappers) */
import EvccCmsPage from "./pages/evcc/EvccCmsPage";
import CustomCssLoader from "./components/cms/CustomCssLoader";
import { useCms } from "./components/cms/useCms";
import TrustedPartnersCmsPage from "./pages/partners/TrustedPartnersCmsPage";
import ReccBenefitsCmsPage from "./pages/benefits/ReccBenefitsCmsPage";
import MemberResourceCmsPage from "./pages/member-resources/MemberResourceCmsPage";


/* Static member resource + EVCC/RECC pages */
import MemberResourcesGuidance from "./pages/member/MemberResourcesGuidance";
import MemberResourcesModelDocuments from "./pages/member/MemberResourcesModelDocuments";
import MemberResourcesMarketingTools from "./pages/member/MemberResourcesMarketingTools";

import TrainingWebinars from "./pages/member/TrainingWebinars";

import TrustedPartnersEvcc from "./pages/partners/TrustedPartnersEvcc";
import ReccMemberDiscounts from "./pages/benefits/ReccMemberDiscounts";

import EvccGuidanceDocuments from "./pages/evcc/EvccGuidanceDocuments";
import EvccModelDocuments from "./pages/evcc/EvccModelDocuments";
import EvccMarketingToolkit from "./pages/evcc/EvccMarketingToolkit";
import EvccTrustedPartners from "./pages/evcc/EvccTrustedPartners";
import EvccMemberDiscounts from "./pages/evcc/EvccMemberDiscounts";

import AdminAssignAwardsPage from "./pages/admin/AdminAssignAwardsPage";
import AdminSurveyFeedbackPage from "./pages/AdminSurveyFeedbackPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";

/* ---------- Lazy-loaded pages (code-splitting) ---------- */
const LoginPage = lazy(() => import("./pages/LoginPage"));
const RegistrationPage = lazy(() => import("./pages/RegistrationPage"));
const EmployeeCertificatePage = lazy(() =>
  import("./pages/member/MemberCertificatePage")
);
const DocumentsPage = lazy(() => import("./pages/DocumentsPage"));
const MyDocumentsPage = lazy(() => import("./pages/MyDocumentsPage"));
const AdminCompanyDocuments = lazy(() =>
  import("./pages/AdminCompanyDocuments")
);

/* Shared */
const PracticeTestsDashboard = lazy(() =>
    import("./pages/member/TrainingModules")
);
const ExamsDashboard = lazy(() => import("./pages/member/ExamsDashboard"));
const TakeTestPage = lazy(() => import("./pages/member/TakeTestPage"));
const MyPaymentsPage = lazy(() => import("./pages/MyPaymentsPage"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));

/* Admin */
const AdminLayout = lazy(() => import("./layouts/layouts/AdminLayout"));
const AdminDashboardPage = lazy(() => import("./pages/AdminDashboardPage"));
const CompanyAwardsPage = lazy(() =>
  import("./pages/admin/CompanyAwardsPage")
);
const AdminUserAnswersPage = lazy(() =>
  import("./pages/AdminUserAnswersPage")
);
const UsersManagementPage = lazy(() => import("./pages/UsersManagementPage"));
const UserLogsPage = lazy(() => import("./pages/UserLogsPage"));
const CreateUserPage = lazy(() => import("./pages/CreateUserPage"));
const CreateNotificationPage = lazy(() =>
  import("./pages/CreateNotificationPage")
);
const CreatePaymentPage = lazy(() => import("./pages/CreatePaymentPage"));
const PaymentsListPage = lazy(() => import("./pages/PaymentsListPage"));

/* Member */
const MemberLayout = lazy(() => import("./layouts/layouts/MemberLayout"));
const MemberDashboardPage = lazy(() => import("./pages/MemberDashboardPage"));
const MemberDocumentsPage = lazy(() =>
  import("./pages/member/MemberDocumentsPage")
);

/* Company Admin */
const CompanyAdminLayouts = lazy(() =>
  import("./layouts/layouts/CompanyAdminLayouts")
);
const CompanyAdminDashboard = lazy(() =>
  import("./pages/CompanyAdmin/CompanyAdminDashboard")
);
const CompanyProfilePage = lazy(() =>
  import("./pages/CompanyAdmin/CompanyProfilePage")
);
const CompanyAdminUsersPage = lazy(() =>
  import("./pages/CompanyAdmin/CompanyAdminUsersPage")
);
const RequestDocumentsPage = lazy(() =>
  import("./pages/CompanyAdmin/RequestDocumentsPage")
);

/* New: Portal document viewer page (frontend-protected route) */
const PortalDocumentPage = lazy(() =>
  import("./pages/member/PortalDocumentPage")
);

/* ---------- Small helpers ---------- */
const RouteLoader = () => <div style={{ padding: 24 }}>Loading…</div>;
const NotFound = () => <Navigate to="/" replace />;

/** Renders <Route> children from a descriptor array */
const renderShared = (defs) =>
  defs.map(({ path, element }) => (
    <Route key={path} path={path} element={element} />
  ));

/** Shared routes used by BOTH Member and Company Admin sections. */
const SHARED_MEMBER_COMPANY_ROUTES = [
  /*{ path: "documents", element: <MemberDocumentsPage /> },*/
  { path: "practice-tests", element: <PracticeTestsDashboard /> },
  { path: "exams", element: <ExamsDashboard /> },
  { path: "take-test", element: <TakeTestPage /> }, // nested version still supported
  { path: "my-payments", element: <MyPaymentsPage /> },
];

/**
 * Helper: when someone hits /practice-tests or /exams without /member or /company
 * redirect them to the correct base based on their role.
 */
const PracticeTestsEntry = () => {
  const { user } = useAuth();
  const basePath = user?.role === "company_admin" ? "/company" : "/member";
  return <Navigate to={`${basePath}/practice-tests`} replace />;
};

const ExamsEntry = () => {
  const { user } = useAuth();
  const basePath = user?.role === "company_admin" ? "/company" : "/member";
  return <Navigate to={`${basePath}/exams`} replace />;
};

export default function App() {
  const CMS_KEYS = useMemo(() => ["site.custom_css_url"], []);
  const { values } = useCms(CMS_KEYS, "");

  return (
    <AuthProvider>
      <CustomCssLoader href={values?.["site.custom_css_url"]} />
      <NotificationProvider>
        <BrowserRouter>
          <Suspense fallback={<RouteLoader />}>
            <Routes>
              {/* -------- Public -------- */}
              <Route path="/" element={<LoginPage />} />
              <Route path="/register" element={<RegistrationPage />} />
              <Route
                path="/member/:id/certificate"
                element={<EmployeeCertificatePage />}
              />
            <Route path="/set-password" element={<SetPassword />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/forgot-password"
                element={<ForgotPasswordPage />}
              />

              {/* -------- Company Admin + Member (both) -------- */}
              <Route
                element={
                  <ProtectedRoute roles={["company_admin", "member"]} />
                }
              >
                {/* ✅ Universal take-test route (can be used from email links) */}
                <Route path="/take-test" element={<TakeTestPage />} />

                {/* ✅ Short aliases that redirect to proper section based on role */}
                <Route path="/practice-tests" element={<PracticeTestsEntry />} />
                <Route path="/exams" element={<ExamsEntry />} />

                <Route path="/company" element={<CompanyAdminLayouts />}>
                  <Route
                    index
                    element={<Navigate to="/company/dashboard" replace />}
                                  />
                                  {/*
                  <Route
                    path="my-documents"
                    element={<MyDocumentsPage />}
                  />*/}
                  <Route
                    path="dashboard"
                    element={<CompanyAdminDashboard />}
                  />
                  <Route
                    path="profile"
                    element={<CompanyProfilePage />}
                  />
                  <Route
                    path="users"
                    element={<CompanyAdminUsersPage />}
                  />
                  <Route
                    path="manage-company"
                    element={<ManageCompanyPage />}
                  />
                  {renderShared(SHARED_MEMBER_COMPANY_ROUTES)}
                  <Route
                    path="request-documents"
                    element={<RequestDocumentsPage />}
                  />
                  <Route
                    path="profile-settings"
                    element={<ProfilePage />}
                  />

                  {/* -------- Member Resources (RECC) -------- */}
                  <Route
                    path="member-resources"
                    element={
                      <Navigate
                        to="/company/member-resources/guidance"
                        replace
                      />
                    }
                                  />
                                  {/*
                  <Route
                    path="member-resources/guidance"
                    element={<MemberResourcesGuidance />}
                  />
                  <Route
                    path="member-resources/model-documents"
                    element={<MemberResourcesModelDocuments />}
                  />
                  <Route
                    path="member-resources/marketing-tools"
                    element={<MemberResourcesMarketingTools />}
                  />*/}

                    <Route
                        path="member-resources/:slug"
                                      element={<MemberResourceCmsPage />}
                    />

                  {/* -------- Training (RECC) -------- */}
                  <Route
                    path="training"
                    element={
                      <Navigate
                        to="/company/training/webinars"
                        replace
                      />
                    }
                  />
                  <Route
                    path="training/webinars"
                    element={<TrainingWebinars />}
                  />
                  {/* Training modules use the Practice Tests dashboard */}
                  <Route
                    path="training/modules"
                    element={<PracticeTestsDashboard />}
                  />

                  {/* -------- Trusted Partners (RECC) -------- */}
                  <Route
                    path="trusted-partners"
                    element={
                      <Navigate
                        to="/company/trusted-partners/evcc"
                        replace
                      />
                    }
                                  />
                {/* 
                  <Route
                    path="trusted-partners/evcc"
                    element={<TrustedPartnersEvcc />}
                  />
                  */ }
                  <Route
                    path="trusted-partners/:slug"
                    element={<TrustedPartnersCmsPage />}
                  />
                  <Route
                    path="trustmark"
                    element={
                      <Navigate
                        to="/company/trusted-partners/trustmark"
                        replace
                      />
                    }
                  />

                  {/* -------- Benefits (RECC) -------- */}
                  <Route
                    path="benefits"
                    element={
                      <Navigate
                        to="/company/benefits/recc"
                        replace
                      />
                    }
                  />
                  <Route
                    path="benefits/:slug"
                    element={<ReccBenefitsCmsPage />}
                  />

                  {/* -------- EVCC -------- */}
                  <Route
                    path="evcc"
                    element={
                      <Navigate
                        to="/company/evcc/guidance-documents"
                        replace
                      />
                    }
                  />
                  {/** 
                  <Route
                    path="evcc/guidance-documents"
                    element={<EvccGuidanceDocuments />}
                  />
                  
                  <Route
                    path="evcc/model-documents"
                    element={<EvccModelDocuments />}
                  />
                  <Route
                    path="evcc/marketing-toolkit"
                    element={<EvccMarketingToolkit />}
                  />
                  <Route
                    path="evcc/trusted-partners"
                    element={<EvccTrustedPartners />}
                  />
                  <Route
                    path="evcc/member-discounts"
                    element={<EvccMemberDiscounts />}
                  />
                  */}
                  {/* Fallback to CMS-backed EVCC pages (e.g. /evcc/overview) */}
                  <Route
                    path="evcc/:slug"
                    element={<EvccCmsPage />}
                  />
                  {/* Legacy path pattern for EVCC content under trusted-partners */}
                  <Route
                    path="trusted-partners/evcc/:slug"
                    element={<EvccCmsPage />}
                  />
                </Route>
              </Route>

              {/* -------- Member ONLY -------- */}
              <Route element={<ProtectedRoute roles={["member"]} />}>
                <Route path="/member" element={<MemberLayout />}>
                  <Route
                    index
                    element={<Navigate to="/member/dashboard" replace />}
                  />
                  <Route
                    path="dashboard"
                    element={<MemberDashboardPage />}
                  />
                  {renderShared(SHARED_MEMBER_COMPANY_ROUTES)}
                  <Route path="profile" element={<ProfilePage />} />

                  {/* -------- Member Resources (RECC) -------- */}
                  <Route
                    path="member-resources"
                    element={
                      <Navigate
                        to="/member/member-resources/guidance"
                        replace
                      />
                    }
                  />
                  <Route
                    path="member-resources/guidance"
                    element={<MemberResourcesGuidance />}
                  />
                  <Route
                    path="member-resources/model-documents"
                    element={<MemberResourcesModelDocuments />}
                  />
                  <Route
                    path="member-resources/marketing-tools"
                    element={<MemberResourcesMarketingTools />}
                  />

                  {/* -------- Training (RECC) -------- */}
                  <Route
                    path="training"
                    element={
                      <Navigate
                        to="/member/training/webinars"
                        replace
                      />
                    }
                  />
                  <Route
                    path="training/webinars"
                    element={<TrainingWebinars />}
                  />
                  {/* Training modules use the Practice Tests dashboard */}
                  <Route
                    path="training/modules"
                    element={<PracticeTestsDashboard />}
                  />

                  {/* -------- Trusted Partners (RECC) -------- */}
                  <Route
                    path="trusted-partners"
                    element={
                      <Navigate
                        to="/member/trusted-partners/evcc"
                        replace
                      />
                    }
                  />
                  <Route
                    path="trusted-partners/evcc"
                    element={<TrustedPartnersEvcc />}
                  />
                  <Route
                    path="trusted-partners/:slug"
                    element={<TrustedPartnersCmsPage />}
                  />
                  <Route
                    path="trustmark"
                    element={
                      <Navigate
                        to="/member/trusted-partners/trustmark"
                        replace
                      />
                    }
                  />

                  {/* -------- Benefits (RECC) -------- */}
                  <Route
                    path="benefits"
                    element={
                      <Navigate
                        to="/member/benefits/recc"
                        replace
                      />
                    }
                  />
                  <Route
                    path="benefits/recc"
                    element={<ReccMemberDiscounts />}
                  />
                  <Route
                    path="benefits/:slug"
                    element={<ReccBenefitsCmsPage />}
                  />

                  {/* -------- EVCC -------- */}
                  <Route
                    path="evcc"
                    element={
                      <Navigate
                        to="/member/evcc/guidance-documents"
                        replace
                      />
                    }
                  />
                  <Route
                    path="evcc/guidance-documents"
                    element={<EvccGuidanceDocuments />}
                  />
                  <Route
                    path="evcc/model-documents"
                    element={<EvccModelDocuments />}
                  />
                  <Route
                    path="evcc/marketing-toolkit"
                    element={<EvccMarketingToolkit />}
                  />
                  <Route
                    path="evcc/trusted-partners"
                    element={<EvccTrustedPartners />}
                  />
                  <Route
                    path="evcc/member-discounts"
                    element={<EvccMemberDiscounts />}
                  />
                  {/* Fallback to CMS-backed EVCC pages */}
                  <Route
                    path="evcc/:slug"
                    element={<EvccCmsPage />}
                  />
                  <Route
                    path="trusted-partners/evcc/:slug"
                    element={<EvccCmsPage />}
                  />
                </Route>
              </Route>

              {/* -------- Admin ONLY -------- */}
              <Route element={<ProtectedRoute roles={["admin"]} />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route
                    index
                    element={<Navigate to="/admin/dashboard" replace />}
                  />
                  <Route
                    path="dashboard"
                    element={<AdminDashboardPage />}
                  />
                  <Route
                    path="awards"
                    element={<AdminAssignAwardsPage />}
                  />
                  <Route
                    path="survey-results"
                    element={<AdminSurveyFeedbackPage />}
                  />
                  <Route path="cms" element={<CmsEditor />} />
                  <Route
                    path="question-bank"
                    element={<QuestionBank />}
                  />
                  <Route
                    path="companies"
                    element={<AdminCompanyDocuments />}
                  />
                  <Route
                    path="user-answers"
                    element={<AdminUserAnswersPage />}
                  />
                  <Route path="documents" element={<DocumentsPage />} />
                  <Route
                    path="create-user"
                    element={<CreateUserPage />}
                  />
                  <Route path="users" element={<UsersManagementPage />} />
                  <Route
                    path="manage/companies"
                    element={<AdminCompaniesPage />}
                  />
                  <Route
                    path="users/:id/logs"
                    element={<UserLogsPage />}
                  />
                  <Route
                    path="notifications/create"
                    element={<CreateNotificationPage />}
                  />
                  <Route path="profile" element={<ProfilePage />} />
                  <Route
                    path="payments"
                    element={<PaymentsListPage />}
                  />
                  <Route
                    path="users/:id/create-payment"
                    element={<CreatePaymentPage />}
                  />
                </Route>
              </Route>

              {/* -------- Any authenticated user -------- */}
              <Route element={<ProtectedRoute />}>
                {/* New: frontend-protected portal document route */}
                <Route
                  path="/portal/documents/:id"
                  element={<PortalDocumentPage />}
                />
                <Route path="/documents" element={<DocumentsPage />} />
              </Route>

              {/* -------- 404 fallback -------- */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </NotificationProvider>
    </AuthProvider>
  );
}
